import { CoreRoot } from '../../../core-logic/CoreRoot';
import {
  IAppLoaderNavigationService,
  RouteID,
  RouterActions,
  RouteView,
  RouteParams,
} from '../../../../infrastructure/types/features/navigation/navigation-types';
import { routesConfig } from '../../../../infrastructure/utils/navigation/routes-config';
import { IAppLoaderStorage } from '../../../../infrastructure/types/utils/storage-types';
import { autorun, makeAutoObservable, runInAction } from 'mobx';
import { UIRoot } from '../../UiRoot';
import { UserResponse } from '../../../../../shared/infrastructure/types/common/common-contracts';

class RoutingModule {
  currentView: RouteView | null = null;
  protected appStorage: IAppLoaderStorage;
  protected currentUser?: UserResponse;
  protected navigationService: IAppLoaderNavigationService;

  constructor(
    coreRoot: CoreRoot,
    uiRoot: UIRoot,
    navigationService: IAppLoaderNavigationService,
    appStorage: IAppLoaderStorage,
    currentUser?: UserResponse,
  ) {
    makeAutoObservable(this, {});

    this.navigationService = navigationService;
    this.appStorage = appStorage;
    this.currentUser = currentUser;
  }

  initRouter() {
    this.navigationService.initRouter(this.getRouterActions());

    autorun(() => {
      const view = this.currentView;
      const path = this.currentPath;
      const location = window.location.hash.replace('#', '');
      if (!view || view.id === RouteID.ROOT || view.id === RouteID.NOT_FOUND) {
        return;
      }
      if (path === location || location.startsWith(`${path}?`)) {
        return;
      }
      this.navigationService.navigate(path as RoutePath, false);
    });
  }

  getRouterActions(): RouterActions {
    return {
      navigateToRoute: (routeID: RouteID, params?: RouteParams) => {
        runInAction(() => {
          this.navigateToRoute(routeID, params);
        });
      },
    };
  }

  get currentPath(): RouteID | RoutePath {
    if (!this.currentView) return RouteID.ROOT;

    if (this.currentView.path) {
      return this.currentView.path;
    }

    return this.currentView.id;
  }

  navigateToRoute(routeID: RouteID, params?: RouteParams) {
    const routeConfig = routesConfig.find(route => route.id === routeID);
    let path: RoutePath;

    if (routeConfig?.pathBuilder && params) {
      path = routeConfig.pathBuilder(params);
    } else {
      path = routeID as RoutePath;
    }

    this.currentView = { id: routeID, path, params };
  }

  openRoot() {
    this.navigateToRoute(RouteID.ROOT);
  }

  openLogin() {
    this.navigateToRoute(RouteID.LOGIN);
  }

  openRegister() {
    this.navigateToRoute(RouteID.REGISTER);
  }

  openRegisterEmail() {
    this.navigateToRoute(RouteID.REGISTER_EMAIL);
  }

  openRegisterPhone(phone?: string) {
    if (phone) {
      this.navigateToRoute(RouteID.REGISTER_PHONE_PARAM, { phone });
    } else {
      this.navigateToRoute(RouteID.REGISTER_PHONE);
    }
  }

  openRecovery() {
    this.navigateToRoute(RouteID.RECOVERY);
  }

  openRecoveryReset(resetPasswordToken?: string) {
    this.navigateToRoute(RouteID.RECOVERY_RESET, { resetPasswordToken });
  }

  openInvitations(invitationToken?: string) {
    this.navigateToRoute(RouteID.INVITATIONS, { invitationToken });
  }

  openTemporarySignIn(token: string) {
    this.navigateToRoute(RouteID.TEMPORARY_SIGN_IN, { token });
  }

  openNotFound() {
    this.navigateToRoute(RouteID.NOT_FOUND);
  }
}

export { RoutingModule };
